/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2018 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H217483 on 4/11/2019.
 */

import React from 'react'
import {injectIntl} from 'react-intl';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';


class LearningLoopCountChart extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            width: 0,
            height: 0,
            breakpoints: null,
            activeBreakpoint: null,
            activeTooltip: null,
            visibleSeries: {
                closed: true,
                exported: true
            }
        };

        this.largeChartRef = React.createRef();
        this.smallChartRef = React.createRef();
    }

    handleBarInteraction = (chartKey, seriesKey, data, event) => {
        if (!data || !data.payload) {
            return;
        }

        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        const containerRef = chartKey === 'large' ? this.largeChartRef : this.smallChartRef;
        const container = containerRef.current;
        const containerWidth = container ? container.getBoundingClientRect().width : 0;
        const tooltipWidth = 220;
        const tooltipPadding = 12;
        const idealLeft = data.x + (data.width / 2);
        const minLeft = tooltipWidth / 2 + tooltipPadding;
        const maxLeft = containerWidth > 0 ? containerWidth - (tooltipWidth / 2) - tooltipPadding : idealLeft;

        this.setState({
            activeTooltip: {
                chartKey,
                seriesKey,
                left: Math.max(minLeft, Math.min(idealLeft, maxLeft)),
                top: data.y,
                name: seriesKey === 'closed' ? this.getClosedLabel() : this.getExportedLabel(),
                value: data.payload[seriesKey]
            }
        });
    }

    handleSeriesToggle = (seriesKey, checked) => {
        this.setState((prevState) => ({
            visibleSeries: {
                ...prevState.visibleSeries,
                [seriesKey]: checked
            },
            activeTooltip: prevState.activeTooltip && prevState.activeTooltip.seriesKey === seriesKey && !checked
                ? null
                : prevState.activeTooltip
        }));
    }

    getClosedLabel() {
        return this.props.intl.formatMessage({
            id: 'dashboard.chart.learningLoop.counts.closedFaults'
        });
    }

    getExportedLabel() {
        return this.props.intl.formatMessage({
            id: 'dashboard.chart.learningLoop.counts.exportedFaults'
        });
    }

    getVisibleMax(chartData) {
        const { visibleSeries } = this.state;

        const maxValue = chartData.reduce((max, item) => {
            const visibleClosed = visibleSeries.closed ? item.closed : 0;
            const visibleExported = visibleSeries.exported ? item.exported : 0;
            return Math.max(max, visibleClosed, visibleExported);
        }, 0);

        return Math.max(2, Math.ceil(maxValue / 2) * 2);
    }

    clearTooltip = () => {
        this.setState({ activeTooltip: null });
    }

    renderTooltip() {
        const { activeTooltip } = this.state;

        if (!activeTooltip) {
            return null;
        }

        return (
            <div
                className="learning-loop-count-chart-tooltip"
                style={{
                    left: activeTooltip.left,
                    top: activeTooltip.top
                }}
            >
                <span
                    className={
                        'learning-loop-count-chart-tooltip-dot ' +
                        (activeTooltip.seriesKey === 'closed'
                            ? 'learning-loop-count-chart-tooltip-dot--closed'
                            : 'learning-loop-count-chart-tooltip-dot--exported')
                    }
                />
                <span>
                    {activeTooltip.name}: {Number(activeTooltip.value).toFixed(2)}
                </span>
            </div>
        );
    }

    renderSeriesControls() {
        const { visibleSeries } = this.state;

        return (
            <div
                className="learning-loop-count-chart-controls"
            >
                <button
                    type="button"
                    className={
                        'learning-loop-count-chart-controls-item learning-loop-count-chart-controls-item--closed ' +
                        (visibleSeries.closed ? 'is-active' : 'is-inactive')
                    }
                    onClick={() => this.handleSeriesToggle('closed', !visibleSeries.closed)}
                    aria-pressed={visibleSeries.closed}
                >
                    <span className="learning-loop-count-chart-controls-swatch" />
                    <span className="learning-loop-count-chart-controls-text">
                        {this.getClosedLabel()}
                    </span>
                </button>
                <button
                    type="button"
                    className={
                        'learning-loop-count-chart-controls-item learning-loop-count-chart-controls-item--exported ' +
                        (visibleSeries.exported ? 'is-active' : 'is-inactive')
                    }
                    onClick={() => this.handleSeriesToggle('exported', !visibleSeries.exported)}
                    aria-pressed={visibleSeries.exported}
                >
                    <span className="learning-loop-count-chart-controls-swatch" />
                    <span className="learning-loop-count-chart-controls-text">
                        {this.getExportedLabel()}
                    </span>
                </button>
            </div>
        );
    }

    render() {
        const { intl, dayCounts } = this.props;

        const chartDataLarge = dayCounts.map(d => ({
            date: intl.formatDate(d.date, {
                month: 'short',
                day: 'numeric'
            }),
            closed: d.closedCount,
            exported: d.exportedCount
        }));

        const yAxisMax = this.getVisibleMax(chartDataLarge);
        const yAxisTicks = Array.from({ length: yAxisMax + 1 }, (_, index) => index);

        const closedLabel = this.getClosedLabel();
        const exportedLabel = this.getExportedLabel();

        const dayCountsSmall = dayCounts.slice(dayCounts.length - 5, dayCounts.length);

        const chartDataSmall = dayCountsSmall.map(d => ({
            date: intl.formatDate(d.date, {
                month: 'short',
                day: 'numeric'
            }),
            closed: d.closedCount,
            exported: d.exportedCount
        }));

        return (
            <div className="learning-loop-count-chart-wrapper" onClick={this.clearTooltip}>
                <div className="chart-large">
                    <p className="chart-title">
                        {intl.formatMessage({
                            id: 'dashboard.chart.learningLoop.counts'
                        })}
                    </p>

                    <p className="chart-subtitle">
                        {intl.formatMessage({
                            id: 'dashboard.chart.learningLoop.counts.subtitle.large'
                        })}
                    </p>

                    <div
                        className="learning-loop-count-chart-plot"
                        ref={this.largeChartRef}
                    >
                        {this.state.activeTooltip && this.state.activeTooltip.chartKey === 'large' && this.renderTooltip()}

                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                                data={chartDataLarge}
                            >
                            <CartesianGrid vertical={false} strokeWidth={2} />

                            <XAxis
                                dataKey="date"
                                allowDecimals={false}
                            />

                            <YAxis
                                allowDecimals={false}
                                domain={[0, yAxisMax]}
                                ticks={yAxisTicks}
                                width={28}
                                tickMargin={6}
                                axisLine={false}
                                tickLine={false}
                            />

                            <Bar
                                dataKey="closed"
                                name={closedLabel}
                                fill="#7AB548"
                                barSize={20}
                                hide={!this.state.visibleSeries.closed}
                                onMouseEnter={(data, index, event) => this.handleBarInteraction('large', 'closed', data, event)}
                                onMouseLeave={this.clearTooltip}
                                onClick={(data, index, event) => this.handleBarInteraction('large', 'closed', data, event)}
                            />

                            <Bar
                                dataKey="exported"
                                name={exportedLabel}
                                fill="#2293E0"
                                barSize={20}
                                hide={!this.state.visibleSeries.exported}
                                onMouseEnter={(data, index, event) => this.handleBarInteraction('large', 'exported', data, event)}
                                onMouseLeave={this.clearTooltip}
                                onClick={(data, index, event) => this.handleBarInteraction('large', 'exported', data, event)}
                            />
                            </BarChart>
                        </ResponsiveContainer>

                        {this.renderSeriesControls()}
                    </div>
                </div>

                <div className="chart-small">
                    <p className="chart-title">
                        {intl.formatMessage({
                            id: 'dashboard.chart.learningLoop.counts'
                        })}
                    </p>

                    <p className="chart-subtitle">
                        {intl.formatMessage({
                            id: 'dashboard.chart.learningLoop.counts.subtitle.small'
                        })}
                    </p>

                    <div
                        className="learning-loop-count-chart-plot"
                        ref={this.smallChartRef}
                    >
                        {this.state.activeTooltip && this.state.activeTooltip.chartKey === 'small' && this.renderTooltip()}

                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={chartDataSmall}
                                layout="vertical"
                                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                            >
                            <CartesianGrid horizontal={false} strokeWidth={2} />

                            <XAxis
                                type="number"
                                allowDecimals={false}
                            />

                            <YAxis
                                type="category"
                                dataKey="date"
                                allowDecimals={false}
                                width={42}
                                tickMargin={6}
                                axisLine={false}
                                tickLine={false}
                            />

                            <Bar
                                dataKey="closed"
                                name={closedLabel}
                                fill="#7AB548"
                                hide={!this.state.visibleSeries.closed}
                                onMouseEnter={(data, index, event) => this.handleBarInteraction('small', 'closed', data, event)}
                                onMouseLeave={this.clearTooltip}
                                onClick={(data, index, event) => this.handleBarInteraction('small', 'closed', data, event)}
                            />

                            <Bar
                                dataKey="exported"
                                name={exportedLabel}
                                fill="#2293E0"
                                hide={!this.state.visibleSeries.exported}
                                onMouseEnter={(data, index, event) => this.handleBarInteraction('small', 'exported', data, event)}
                                onMouseLeave={this.clearTooltip}
                                onClick={(data, index, event) => this.handleBarInteraction('small', 'exported', data, event)}
                            />
                            </BarChart>
                        </ResponsiveContainer>

                        {this.renderSeriesControls()}
                    </div>
                </div>
            </div>
        );
    }
}


export default injectIntl(LearningLoopCountChart)